/**
 * Sak's side of the suite shelf — the synthetic shared folder every hosted
 * pane sees (see ortam/suite/bridge/src/shelf.ts).
 *
 * Vault files are server-backed, so they can never be handed to the editors
 * as directory handles. Instead the bytes are fetched through sak's own API
 * (same-origin, bearer token — no bridge involved) and staged onto the shelf
 * with a single `fs:call writeBinary` over the existing pane bridge.
 */

import { createSuiteShelfAdapter } from '@suite/bridge';
import { driveClient } from '@/lib/drive/driveClient';

/** Application id this pane uses on the bridge — matches sak's tile. */
const APP_ID = 'sak';

/** Last segment of a drive path, used as the shelf file name. */
function fileNameOf(path: string): string {
  const parts = String(path ?? '').split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
}

/**
 * Stage one vault file onto the shelf under `name` (default: its own).
 * Answers false when either hop fails; the caller decides how to report it.
 */
export async function stageVaultFile(path: string, name?: string): Promise<boolean> {
  try {
    // Sak's own download endpoint — the bytes never leave this origin.
    const blob = await driveClient.readBlob(path);
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const adapter = createSuiteShelfAdapter(APP_ID);
    await adapter.initialize();
    await adapter.writeBinary(`/${name || fileNameOf(path)}`, bytes);
    return true;
  } catch (err) {
    console.warn('[suite-shelf] staging failed:', err instanceof Error ? err.message : String(err));
    return false;
  }
}

/** File names currently staged on the shelf, as seen from this pane. */
export async function listShelfFiles(): Promise<string[]> {
  try {
    const adapter = createSuiteShelfAdapter(APP_ID);
    await adapter.initialize();
    return await adapter.list('/');
  } catch (err) {
    console.warn('[suite-shelf] listing failed:', err instanceof Error ? err.message : String(err));
    return [];
  }
}
